import { EmptyState, Panel, Tab, TabList, TabPanel, Tabs } from '@hgc/ui';
import { useCallback, useState, type JSX } from 'react';

import { PlannedWorkCard, SectionHeader } from '../app/sections/Placeholder';
import { ParameterEditorScreen } from './params/ParameterEditor';
import { useParameterSession } from './params/useParameterSession';
import { AppSettingsScreen } from './settings/AppSettingsPanel';
import { VehicleSummaryPanel } from './summary/VehicleSummaryPanel';
import {
  DEFAULT_SETUP_TAB,
  SETUP_TABS,
  isSetupTab,
  type SetupTab,
  type SetupTabDefinition,
} from './tabs';

/**
 * 设置分区的框架。
 *
 * 它把三件已完成的能力（参数编辑器、机体摘要、应用设置）接成一个可导航的整体，
 * 其余子页**诚实地留空**：每一页都写清"会提供什么、为什么现在是空的、由谁填充"。
 * 用假数据把页面填满会让"接线到底通没通"变得无法判断 —— 那正是本阶段要验证的唯一一件事。
 *
 * 参数会话在这里建立一次，然后同时喂给参数页与摘要页：两份会话意味着同一张参数表
 * 在链路上被下载两遍，而且两份"待写入改动"会各自为政。
 */

export function SetupFramework(): JSX.Element {
  const [tab, setTab] = useState<SetupTab>(DEFAULT_SETUP_TAB);
  const { session, unavailableReason } = useParameterSession();

  const openTab = useCallback((next: SetupTab) => setTab(next), []);

  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title="设置"
        summary="参数、机体摘要、传感器校准与应用设置。尚未实现的子页会写清原因，不会用假数据占位。"
      />

      <Tabs value={tab} onChange={(value) => setTab(isSetupTab(value) ? value : DEFAULT_SETUP_TAB)}>
        <TabList aria-label="设置子页">
          {SETUP_TABS.map((definition) => (
            <Tab key={definition.id} value={definition.id}>
              {definition.state === 'planned' ? `${definition.label}（待实现）` : definition.label}
            </Tab>
          ))}
        </TabList>

        <TabPanel value="summary">
          <VehicleSummaryPanel
            session={session}
            sessionReason={unavailableReason}
            onOpenTab={openTab}
          />
        </TabPanel>

        <TabPanel value="params">
          {session === null ? (
            <Panel title="参数编辑器">
              <EmptyState
                level="caution"
                title="还没有可读参数的飞控"
                description={
                  unavailableReason ??
                  '参数归飞控组件所有：连上飞控之后这一页会自动出现参数表。'
                }
              />
            </Panel>
          ) : (
            <ParameterEditorScreen session={session} />
          )}
        </TabPanel>

        <TabPanel value="app">
          <AppSettingsScreen />
        </TabPanel>

        {SETUP_TABS.filter((definition) => definition.state === 'planned').map((definition) => (
          <TabPanel key={definition.id} value={definition.id}>
            <PlannedPanel definition={definition} />
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}

/**
 * 尚未实现的子页。
 *
 * `emptyReason` 必须回答"为什么现在是空的"，`items` 给出"会提供什么 + 由谁提供" ——
 * 一个空面板如果不说清这两件事，用户唯一的结论是"这个软件坏了"。
 */
function PlannedPanel({ definition }: { readonly definition: SetupTabDefinition }): JSX.Element {
  const plan = definition.plan;
  return (
    <div className="flex flex-col gap-3">
      <Panel title={`${definition.label}：为什么现在是空的`} description={definition.hint}>
        <EmptyState
          level="info"
          title={`${definition.label}尚未实现`}
          description={plan?.missingBecause ?? '这一页还没有实现，原因尚未记录。'}
        />
      </Panel>

      {plan === null ? null : (
        <PlannedWorkCard items={[{ title: plan.willProvide, owner: plan.owner }]} />
      )}
    </div>
  );
}
